import type { Recurrence } from "../api/types";
import { MONTHS_LONG, WEEKDAYS_LONG, daysInMonth, parts, weekday, type DateStr } from "./date";

export const WEEKDAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const ORDINAL = ["", "primeira", "segunda", "terceira", "quarta"];

/** Which "Nth weekday" of its month a date is: 1..4, plus -1 when it's also the last one. */
export function nthOfMonth(date: DateStr): { pos: number; isLast: boolean } {
  const { y, m, d } = parts(date);
  return { pos: Math.ceil(d / 7), isLast: d + 7 > daysInMonth(y, m) };
}

function sameRule(a: Recurrence | null | undefined, b: Recurrence | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return (
    a.frequency === b.frequency &&
    (a.interval ?? 1) === (b.interval ?? 1) &&
    (a.by_weekday ?? []).join() === (b.by_weekday ?? []).join() &&
    (a.by_set_pos ?? 0) === (b.by_set_pos ?? 0) &&
    (a.by_month_day ?? 0) === (b.by_month_day ?? 0) &&
    !a.end_date === !b.end_date &&
    !a.count === !b.count
  );
}

export interface RecurrencePreset {
  label: string;
  rule: Recurrence | null;
}

/** The quick options of the recurrence dropdown, for an event starting on `date`. */
export function recurrencePresets(date: DateStr): RecurrencePreset[] {
  const dow = weekday(date);
  const { y, m, d } = parts(date);
  const { pos, isLast } = nthOfMonth(date);
  const presets: RecurrencePreset[] = [
    { label: "Não se repete", rule: null },
    { label: "Todos os dias", rule: { frequency: "daily" } },
    { label: `Semanal: cada ${WEEKDAYS_LONG[dow]}`, rule: { frequency: "weekly", by_weekday: [WEEKDAY_CODES[dow]] } }
  ];
  if (pos <= 4) presets.push({ label: `Mensal no(a) ${ORDINAL[pos]} ${WEEKDAYS_LONG[dow]}`, rule: { frequency: "monthly", by_weekday: [WEEKDAY_CODES[dow]], by_set_pos: pos } });
  if (isLast) presets.push({ label: `Mensal no(a) última ${WEEKDAYS_LONG[dow]}`, rule: { frequency: "monthly", by_weekday: [WEEKDAY_CODES[dow]], by_set_pos: -1 } });
  if (d === daysInMonth(y, m)) presets.push({ label: "Mensal no último dia do mês", rule: { frequency: "monthly", by_month_day: -1 } });
  presets.push(
    { label: `Anual em ${d} de ${MONTHS_LONG[m - 1]}`, rule: { frequency: "yearly" } },
    { label: "Todos os dias da semana (segunda a sexta-feira)", rule: { frequency: "weekly", by_weekday: ["MO", "TU", "WE", "TH", "FR"] } }
  );
  return presets;
}

export function presetIndex(rule: Recurrence | null | undefined, date: DateStr): number {
  return recurrencePresets(date).findIndex((p) => sameRule(p.rule, rule));
}

const SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** PT-BR description, same wording family as the backend's. */
export function describeRule(rule: Recurrence, start: DateStr): string {
  const every = rule.interval && rule.interval > 1 ? rule.interval : 1;
  const { m, d } = parts(start);
  let text: string;
  switch (rule.frequency) {
    case "daily":
      text = every === 1 ? "Todos os dias" : `A cada ${every} dias`;
      break;
    case "weekly": {
      const days = (rule.by_weekday?.length ? rule.by_weekday : [WEEKDAY_CODES[weekday(start)]]).map((c) => WEEKDAY_CODES.indexOf(c));
      const ordered = [...days.filter((i) => i !== 0).sort(), ...days.filter((i) => i === 0)];
      const list = ordered.join() === "1,2,3,4,5" ? "dias úteis" : ordered.map((i) => SHORT[i]).join(", ");
      text = every === 1 ? `Semanal: ${list}` : `A cada ${every} semanas: ${list}`;
      break;
    }
    case "monthly":
      if (rule.by_set_pos && rule.by_weekday?.length === 1) {
        const i = WEEKDAY_CODES.indexOf(rule.by_weekday[0]);
        const which = rule.by_set_pos === -1 ? "última" : ORDINAL[rule.by_set_pos];
        text = `${every === 1 ? "Mensal" : `A cada ${every} meses`} no(a) ${which} ${WEEKDAYS_LONG[i]}`;
      } else if (rule.by_month_day === -1) {
        text = `${every === 1 ? "Mensal" : `A cada ${every} meses`} no último dia`;
      } else {
        text = `${every === 1 ? "Mensal" : `A cada ${every} meses`} no dia ${d}`;
      }
      break;
    case "yearly":
      text = `${every === 1 ? "Anual" : `A cada ${every} anos`} em ${d} de ${MONTHS_LONG[m - 1]}`;
      break;
  }
  if (rule.end_date) {
    const e = parts(rule.end_date);
    text += `, até ${String(e.d).padStart(2, "0")}/${String(e.m).padStart(2, "0")}/${e.y}`;
  } else if (rule.count) {
    text += `, ${rule.count} vezes`;
  }
  return text;
}
